/**
 * Custom API error class with status code and error code
 */
export class ApiError extends Error {
  statusCode: number;
  code: string;
  details?: Record<string, any>;

  /**
   * Create a new API error
   *
   * @param message Error message
   * @param statusCode HTTP status code
   * @param code Error code for client
   * @param details Additional error details
   */
  constructor(
    message: string,
    statusCode: number = 500,
    code: string = 'INTERNAL_SERVER_ERROR',
    details?: Record<string, any>
  ) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.code = code;
    this.details = details;

    // Capture stack trace
    Error.captureStackTrace(this, this.constructor);
  }

  /**
   * Create a not found error
   *
   * @param message Error message
   * @param code Error code
   * @returns ApiError
   */
  static notFound(message: string = 'Resource not found', code: string = 'RESOURCE_NOT_FOUND'): ApiError {
    return new ApiError(message, 404, code);
  }

  /**
   * Create a bad request error
   *
   * @param message Error message
   * @param code Error code
   * @param details Additional error details
   * @returns ApiError
   */
  static badRequest(
    message: string = 'Bad request',
    code: string = 'BAD_REQUEST',
    details?: Record<string, any>
  ): ApiError {
    return new ApiError(message, 400, code, details);
  }

  /**
   * Create an unauthorized error
   *
   * @param message Error message
   * @param code Error code
   * @returns ApiError
   */
  static unauthorized(
    message: string = 'Unauthorized',
    code: string = 'UNAUTHORIZED'
  ): ApiError {
    return new ApiError(message, 401, code);
  }

  /**
   * Create a forbidden error
   *
   * @param message Error message
   * @param code Error code
   * @returns ApiError
   */
  static forbidden(
    message: string = 'Forbidden',
    code: string = 'FORBIDDEN'
  ): ApiError {
    return new ApiError(message, 403, code);
  }

  /**
   * Create a conflict error
   *
   * @param message Error message
   * @param code Error code
   * @returns ApiError
   */
  static conflict(
    message: string = 'Conflict',
    code: string = 'CONFLICT'
  ): ApiError {
    return new ApiError(message, 409, code);
  }

  /**
   * Create a validation error
   *
   * @param message Error message
   * @param details Validation error details
   * @returns ApiError
   */
  static validation(
    message: string = 'Validation error',
    details?: Record<string, any>
  ): ApiError {
    return new ApiError(message, 422, 'VALIDATION_ERROR', details);
  }

  /**
   * Create an internal server error
   *
   * @param message Error message
   * @param code Error code
   * @returns ApiError
   */
  static internal(
    message: string = 'Internal server error',
    code: string = 'INTERNAL_SERVER_ERROR'
  ): ApiError {
    return new ApiError(message, 500, code);
  }

  /**
   * Create a service unavailable error
   *
   * @param message Error message
   * @param code Error code
   * @returns ApiError
   */
  static serviceUnavailable(
    message: string = 'Service unavailable',
    code: string = 'SERVICE_UNAVAILABLE'
  ): ApiError {
    return new ApiError(message, 503, code);
  }
}

/**
 * Error codes enum
 */
export enum ErrorCode {
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  BAD_REQUEST = 'BAD_REQUEST',
  CONFLICT = 'CONFLICT',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE'
}
